
import { ObjectStringMap } from 'found';
import { compact, invert, values } from 'lodash';

import {
  TaxonomyId, TaxonomyIdToTaxonomyType, TaxonomyType
} from '../../relay/selectors/taxonomy';
import { IQueryStringState, QueryStringFilter, QueryStringFilters, QueryStringParam } from './types';

const searchTypeDelimiter = ':';
export const searchQueryKey = 'keywords' + searchTypeDelimiter;
export const searchFilterExcludeChar = '!';
export const searchDelimiter = ',';

export const TaxonomyQueryStringKey = {
  [TaxonomyType.Lyrics]: 'lyrics' + searchTypeDelimiter,
};

export const QueryStringKeyToTaxonomyType: {[key: string]: TaxonomyType} = invert(TaxonomyQueryStringKey) as any;

export const taxonomyQueryStringKeyRegEx = new RegExp(
  `^(${values(TaxonomyQueryStringKey).join('|')})(.+)`,
);

export const taxonomyIdToQueryKey = (id: number) => {
  return TaxonomyQueryStringKey[TaxonomyIdToTaxonomyType[id]] || 'unknown';
};

export const searchQueryTemplate = ({ searchQuery }: { searchQuery: string }) => {
  // Remove any non-word characters, especially the search delimiter, from the query
  // so there are no parsing issues during query string deserialization
  searchQuery = searchQuery.replace(/[\W]+/g, ' ').trim();
  return `${searchQueryKey}${searchQuery}`;
};

export const searchQueryRegEx = new RegExp(`${searchQueryKey}[\\w ]*`, 'g');

export function getSearchAsArray(query: ObjectStringMap) {
  return compact(query[QueryStringParam.Search].split(searchDelimiter));
}

interface SearchFilterFromQueryStringResults {
  query: string;
  filters: Required<Pick<QueryStringFilters, 'not_term_filters' | 'term_filters'>> & QueryStringFilters;
  taxonomy_query?: GQL.ITaxonomyQuery;
}

function filterIsQuery(filter: string) {
  return filter.indexOf(searchQueryKey) === 0;
}

function filterIsExcluded(filter: string) {
  return filter[0] === searchFilterExcludeChar;
}

export function parseTaxonomyQuery(filter: string): {key: string, value: string} | null {
  const matches = filter.match(taxonomyQueryStringKeyRegEx);
  return matches && {
    key: matches[1],
    value: matches[2],
  };
}

interface ISearchQueryStringFilter extends QueryStringFilter<string[], SearchFilterFromQueryStringResults> {
  searchParamsToArray(params: IQueryStringState | undefined): string[];
  serializeTerm(term: SearchFilter, exclude: boolean): string;
  serializeTaxonomyQuery(term: SearchFilter): string;
}

export const SearchFilter: ISearchQueryStringFilter = {
  searchParamsToArray: (params) => {
    if (params) {
      const { filters, query } = params;
      const searchQuery = query ? [searchQueryTemplate({ searchQuery: query })] : [];

      if (filters) {
        const termFilters = filters.term_filters ?
          filters.term_filters.map((name) => SearchFilter.serializeTerm({name}, false))
          : [];

        const excludedTermFilters = filters.not_term_filters ?
          filters.not_term_filters.map((name) => SearchFilter.serializeTerm({name}, true))
          : [];

        return searchQuery.concat(termFilters, excludedTermFilters);
      }

      return searchQuery;
    }

    return [];
  },
  serialize: (filters) => {
    const encodedFilters = filters.map((filter) => {
      const taxonomyQueryMatch = parseTaxonomyQuery(filter);
      if (filter.indexOf(searchQueryKey) === 0) {
        const keywordsRegExp = new RegExp(`^(${searchQueryKey})(\.+)$`);
        return filter.replace(keywordsRegExp, (_, key, query) => `${key}${encodeURIComponent(query)}`);
      } else if (taxonomyQueryMatch) {
        return filter.replace(taxonomyQueryMatch.value, encodeURIComponent(taxonomyQueryMatch.value));
      }

      return encodeURIComponent(filter);
    });

    return `${encodedFilters}`;
  },
  serializeTerm: (term, exclude) => {
    return exclude ? `${searchFilterExcludeChar}${term.name}` : term.name;
  },
  serializeTaxonomyQuery: (term) => {
    const key = taxonomyIdToQueryKey(term.taxonomy!.id);
    return `${key}${term.name}`;
  },
  unserialize: (query) => {
    let state: SearchFilterFromQueryStringResults = {
      query: '',
      filters: {
        not_term_filters: [],
        term_filters: [],
      },
    };

    if (QueryStringParam.Search in query) {
      const search = getSearchAsArray(query);

      state = search.reduce((acc, filter) => {
        const taxonomyQueryMatch = parseTaxonomyQuery(filter);
        if (filterIsQuery(filter)) {
          return {
            ...acc,
            query: decodeURIComponent(filter.substring(searchQueryKey.length)),
          };
        } else if (filterIsExcluded(filter)) {
          return {
            ...acc,
            filters: {
              ...acc.filters,
              not_term_filters: [
                ...acc.filters.not_term_filters!,
                decodeURIComponent(filter.substring(searchFilterExcludeChar.length)),
              ],
            },
          };
        } else if (taxonomyQueryMatch) {
          return {
            ...acc,
            taxonomy_query: {
              query: decodeURIComponent(taxonomyQueryMatch.value),
              taxonomy_id: TaxonomyId[QueryStringKeyToTaxonomyType[taxonomyQueryMatch.key]],
            },
          };
        } else {
          return {
            ...acc,
            filters: {
              ...acc.filters,
              term_filters: [
                ...acc.filters.term_filters!,
                decodeURIComponent(filter),
              ],
            },
          };
        }
      }, state);
    }

    if (QueryStringParam.Similarity in query) {
      state.filters.similarity = {
        track_id: query[QueryStringParam.Similarity],
        feature: GQL.TrackSimilarityFeature.Default,
      };
    }

    return state;
  },
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/contexts/queryString/searchFilters.ts